import { useCallback, useMemo, useState } from "react";
import { AxiosRequest } from "_services/api";
import { HttpError } from "_services/api/HttpError";
import { HttpResponse } from "_services/api/HttpResponse";
import {
  IData,
  IHttpError,
  IHttpResponse,
} from "_services/api/interface/HttpResponse";
import { IDebitoIpva } from "../interfaces/IDebitoIpva";
import { IFiltroDebitoIpva } from "../interfaces/IFiltroDebitoIpva";
import { toastErro } from "components/Toast";
export function usePagarIpva() {
  const api = useMemo(() => AxiosRequest.makeApi(), []);
  const msAw = useMemo(
    () => AxiosRequest.makeApi(process.env.REACT_APP_MS_AW),
    [],
  );

  const [loading, setLoading] = useState(false);
  const [loadingExportar, setLoadingExportar] = useState(false);
  const [debitos, setDebitos] = useState<IDebitoIpva[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const mapDebito = (item: any): IDebitoIpva => ({
    uid: `${item.id_processo}_${item.exercicio}`,
    id: item.id_processo,
    exercicio: item.exercicio,
    placa: item.placa,
    renavam: item.renavam,
    chassi: item.chassi,
    razaoSocial: item.razao_social,
    dataSolicitacao: item.data_disponibilizacao_ipva ?? null,
    dataVencimento: item.vencimento,
    valor: item.valor,
    statusPagamento: item.statusPagamento,
    statusProcesso: item.statusProcesso,
    motivoErro: item.motivoErro ?? null,
    param: item.param ?? null,
    dataPagamento: item.data_hora_pagamento ?? null,
  });

  const buildParams = (filtros: IFiltroDebitoIpva): string => {
    let params = "";
    params += filtros.idEmpresa ? `&id_empresa=${filtros.idEmpresa}` : "";
    params += filtros.idDivisao ? `&id_loja=${filtros.idDivisao}` : "";
    params += filtros.dadosVeiculos?.length
      ? `&dados_veiculos=${JSON.stringify(filtros.dadosVeiculos)}`
      : "";
    params += filtros.idsProcessos?.length
      ? `&ids_processos=${JSON.stringify(filtros.idsProcessos)}`
      : "";
    if (filtros.statusesPagamento?.length) {
      params += `&pago=${JSON.stringify(filtros.statusesPagamento)}`;
    } else if (
      filtros.statusPagamento !== null &&
      filtros.statusPagamento !== undefined
    ) {
      params += `&pago=${JSON.stringify([filtros.statusPagamento])}`;
    }
    params +=
      filtros.statusProcesso !== null && filtros.statusProcesso !== undefined
        ? `&status_processo=${filtros.statusProcesso}`
        : "";
    params += filtros.inicioVencimento
      ? `&inicio_data_vencimento=${filtros.inicioVencimento.toISOString().split("T")[0]}`
      : "";
    params += filtros.fimVencimento
      ? `&fim_data_vencimento=${filtros.fimVencimento.toISOString().split("T")[0]}`
      : "";
    params += filtros.inicioPagamento
      ? `&inicio_pagamento=${filtros.inicioPagamento.toISOString().split("T")[0]}`
      : "";
    params += filtros.fimPagamento
      ? `&fim_pagamento=${filtros.fimPagamento.toISOString().split("T")[0]}`
      : "";
    return params;
  };

  const listarDebitos = useCallback(
    async (
      filtros: IFiltroDebitoIpva,
      paginaAtual = page,
      limitAtual = limit,
      coluna?: string,
      direcao?: string,
      limparDados = true,
    ): Promise<IHttpResponse<IData | any>> => {
      setLoading(true);
      if (limparDados) {
        setDebitos([]);
        setTotalRows(0);
      }
      try {
        const params = buildParams(filtros);
        const sortParams = coluna && direcao ? `&orderBy=${coluna}&orderDirecao=${direcao.toUpperCase()}` : "";
        const { status, data } = await msAw.get(
          `/debitos/veiculo/financeiro?page=${paginaAtual}&limit=${limitAtual}${params}${sortParams}`,
        );
        setDebitos((data.itens ?? []).map(mapDebito));
        setTotalRows(data.meta?.total ?? 0);
        return new HttpResponse(false, status, data);
      } catch (error: IHttpError<IData, any> | any) {
        setDebitos([]);
        setTotalRows(0);
        return new HttpError<IData>(true, error);
      } finally {
        setLoading(false);
      }
    },
    [api, page, limit],
  );

  const pagarIpva = async (
    processos: { idProcesso: number; exercicio: number }[],
  ): Promise<IHttpResponse<IData | any>> => {
    try {
      const { status, data } = await msAw.post("/pagamento/processo/ipva", {
        processos,
      });
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      return new HttpError<IData>(true, error);
    }
  };

  const salvarPagamentoManual = async (
    idProcesso: number,
    exercicio: number,
    dataPagamento: string,
    comprovante: File,
    observacao: string,
    valorPago: number,
  ): Promise<IHttpResponse<IData | any>> => {
    try {
      const formData = new FormData();
      formData.append("idProcesso", String(idProcesso));
      formData.append("exercicio", String(exercicio));
      formData.append("dataPagamento", dataPagamento);
      formData.append("valorPago", String(valorPago));
      formData.append("comprovante", comprovante);
      if (observacao) formData.append("observacao", observacao);

      const { status, data } = await msAw.post(
        "/pagamento/processo/ipva/manual",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      return new HttpError<IData>(true, error);
    }
  };

  const cancelarDebito = async (
    idProcesso: number,
    exercicio: number,
    observacao: string,
  ): Promise<IHttpResponse<IData | any>> => {
    try {
      const { status, data } = await msAw.post(
        "/pagamento/processo/ipva/cancelar",
        { idProcesso, exercicio, observacao },
      );
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      return new HttpError<IData>(true, error);
    }
  };

  const baixarComprovante = async (
    idProcesso: number,
    exercicio: number,
  ): Promise<void> => {
    try {
      const response = await msAw.get(
        `/debitos/processo/${idProcesso}/comprovante`,
        {
          responseType: "blob",
        },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `comprovante_ipva_${idProcesso}_${exercicio}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toastErro("Erro ao baixar imagem do comprovante");
    }
  };

  const exportarDebitos = async (
    filtros: IFiltroDebitoIpva,
  ): Promise<IHttpResponse<IData | any>> => {
    setLoadingExportar(true);
    try {
      const params = buildParams(filtros);
      const { status, data } = await msAw.get(
        `/debitos/veiculo/financeiro?limit=-1${params}`,
      );
      return new HttpResponse(false, status, {
        ...data,
        itens: (data.itens ?? []).map(mapDebito),
      });
    } catch (error: IHttpError<IData, any> | any) {
      return new HttpError<IData>(true, error);
    } finally {
      setLoadingExportar(false);
    }
  };

  const validarVeiculos = async (
    placasChassisRenavams: string[],
  ): Promise<HttpResponse> => {
    try {
      const { status, data } = await msAw.get(
        `/processo/veiculo/validar/?placasChassisRenavams=${JSON.stringify(placasChassisRenavams)}`,
      );
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      return new HttpError<IData>(true, error);
    }
  };

  const validarProcessos = async (
    processos: string[],
  ): Promise<HttpResponse> => {
    try {
      const { status, data } = await msAw.get(
        `/processo/validar/?processos=${JSON.stringify(processos)}`,
      );
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      return new HttpError<IData>(true, error);
    }
  };

  const onClickProcesso = async (id: number | string) => {
    setLoading(true);
    try {
      const { status, data } = await api.get(`/processos/param/${id}`);
      window.open(
        `${process.env.REACT_APP_DESPACHANTE_BASE_URL}/pages/processo/Processo.php?param=${data}`,
        "_blank",
      );
    } catch (error: IHttpError<IData, any> | any) {
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    setLoading,
    debitos,
    setDebitos,
    totalRows,
    setTotalRows,
    page,
    setPage,
    limit,
    setLimit,
    listarDebitos,
    pagarIpva,
    salvarPagamentoManual,
    cancelarDebito,
    baixarComprovante,
    exportarDebitos,
    validarVeiculos,
    validarProcessos,
    onClickProcesso,
    loadingExportar,
  };
}
